import { AuthClient, type SupportedStorage } from "@supabase/supabase-js";

type ResetSenderOptions = {
  supabaseUrl: string;
  publishableKey: string;
  sessionStorageKey: string;
  storage: SupportedStorage;
  fetch?: typeof fetch;
};

/**
 * Stage the SDK's PKCE verifier until the recovery request succeeds. The SDK
 * otherwise replaces (and on failure deletes) the verifier for an earlier email.
 * This auth-only client never reads or writes the application's session.
 */
export function createPasswordResetSender(options: ResetSenderOptions) {
  const requestStorageKey = `${options.sessionStorageKey}-reset-request`;
  const staged = new Map<string, string>();
  const auth = new AuthClient({
    url: `${options.supabaseUrl}/auth/v1`,
    headers: {
      apikey: options.publishableKey,
      Authorization: `Bearer ${options.publishableKey}`,
    },
    storageKey: requestStorageKey,
    storage: {
      getItem: (key) => staged.get(key) ?? null,
      setItem: (key, value) => { staged.set(key, value); },
      removeItem: (key) => { staged.delete(key); },
    },
    persistSession: true,
    autoRefreshToken: false,
    detectSessionInUrl: false,
    flowType: "pkce",
    fetch: options.fetch,
  });
  let queue: Promise<unknown> = Promise.resolve();

  return (email: string, redirectTo: string) => {
    const send = async () => {
      await auth.initialize();
      // Fail before sending an unusable email if browser storage is blocked.
      const probeKey = `${requestStorageKey}-storage-check`;
      try {
        await options.storage.setItem(probeKey, "1");
        await options.storage.removeItem(probeKey);
      } catch {
        throw new Error("Allow browser storage, then request a new reset link in this browser.");
      }

      try {
        const result = await auth.resetPasswordForEmail(email.trim(), { redirectTo });
        if (!result.error) {
          const verifier = staged.get(`${requestStorageKey}-code-verifier`);
          if (!verifier) throw new Error("Unable to prepare the reset link. Please try again.");
          await options.storage.setItem(`${options.sessionStorageKey}-code-verifier`, verifier);
        }
        return result;
      } finally {
        staged.clear();
      }
    };
    // Serialize use of the staging storage, including requests from two forms.
    const result = queue.then(send);
    queue = result.catch(() => undefined);
    return result;
  };
}
